import React, { createContext, useContext, useState, useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeProviderWrapper = ({ children }) => {
  const [dark, setDark] = useState(true);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: dark ? "dark" : "light",
          primary: {
            main: dark ? "rgb(144, 202, 249)" : "rgb(25, 118, 210)",
          },
          secondary: {
            main: dark ? "#f48fb1" : "hsl(39,100%,92%)",
          },
          orange: dark ? "hsl(39,100%,30%)" : "hsl(39,100%,50%)",
          darkOrange: dark ? "hsl(39,100%,50%)" : "hsl(39,100%,30%)",
          darkPaper: dark ? "hsl(0,0%,32%)" : "hsl(37,100%,92%)",
          darkText: dark ? "white" : "#654321",
          softBackground: dark ? "hsl(0,0%,7%)" : "hsl(39,100%,98%)",
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                margin: 0,
                padding: 0,
                height: "100vh",
                backgroundColor: "background.default",
                color: "text.primary",
              },
              html: {
                margin: 0,
                padding: 0,
                height: "100%",
              },
            },
          },
          MuiButton: {
            defaultProps: {
              variant: "outlined", // Unselected
            },
            styleOverrides: {
              root: {
                // textTransform: "none", // Removes uppercase text transformation
                // fontWeight: "bold",
                borderRadius: "6px",
                padding: "10px 20px",
                border: "1px solid",
                color: dark ? "hsl(39,100%,95%)" : "hsl(0,0%,22%)",
              },
              contained: {
                // Selected
                backgroundColor: dark ? "hsl(39,100%,32%)" : "hsl(39,100%,70%)",
                borderColor: dark ? "hsl(39,100%,32%)" : "hsl(39,100%,70%)",
                // "&:hover": {
                //   backgroundColor: dark ? "hsl(39,100%,32%)" : "hsl(39,100%,50%)",
                //   borderColor: dark ? "hsl(39,100%,92%)" : "hsl(39,100%,50%)",
                // },
              },
              outlined: {
                // Normal (Unselected)
                backgroundColor: dark ? "hsl(39,100%,16%)" : "hsl(39,100%,95%)",
                borderColor: dark ? "hsl(39,100%,32%)" : "hsl(39,100%,70%)",
                "&:hover": {
                  backgroundColor: dark ? "hsl(39,100%,32%)" : "hsl(39,100%,70%)",
                  borderColor: dark ? "hsl(39,100%,32%)" : "hsl(39,100%,70%)",
                },
              },
            },
          },
          MuiAvatar: {
            styleOverrides: {
              root: {
                backgroundColor: dark ? "hsl(39,100%,32%)" : "hsl(39,100%,50%)",
                color: "white",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: dark ? "hsl(0,0%,7%)" : "hsl(39,100%,98%)",
              },
            },
          },
          MuiSlider: {
            styleOverrides: {
              root: {
                color: dark ? "hsl(39,100%,60%)" : "hsl(39,100%,50%)", // Track color
              },
              thumb: {
                backgroundColor: dark ? "hsl(39,100%,80%)" : "hsl(39,100%,50%)", // Handle color
                "&:hover": {
                  backgroundColor: dark ? "hsl(39,100%,90%)" : "rgb(255, 180, 30)",
                },
              },
              track: {
                backgroundColor: dark ? "hsl(39,100%,70%)" : "hsl(39,100%,50%)",
              },
              rail: {
                backgroundColor: dark ? "hsl(39,100%,30%)" : "hsl(39,100%,92%)", // Unfilled track
              },
            },
          },
          MuiSwitch: {
            styleOverrides: {
              root: {
                width: 42,
                height: 26,
                margin: "0px 8px",
                padding: 0,
                "& .MuiSwitch-switchBase": {
                  padding: 1,
                  margin: 2,
                  transitionDuration: "300ms",
                  "&.Mui-checked": {
                    transform: "translateX(16px)",
                    color: "#fff",
                    "& + .MuiSwitch-track": {
                      backgroundColor: dark ? "hsl(39,100%,20%)" : "hsl(39,100%,92%)", // track when Enabled
                      opacity: 1,
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: 22,
                  height: 22,
                  backgroundColor: dark ? "hsl(39,100%,80%)" : "hsl(39,100%,50%)",
                },
                "& .MuiSwitch-track": {
                  borderRadius: 13,
                  backgroundColor: dark ? "hsl(39,100%,20%)" : "hsl(39,100%,92%)",
                  opacity: 1,
                },
              },
            },
          },
          MuiBadge: {
            // If I need to override certain icons, look at Comment in UserDiscuss.js
            styleOverrides: {
              badge: {
                backgroundColor: dark ? "hsl(39,100%,30%)" : "hsl(39,100%,50%)", // Change background color
                color: "white", // Set text color inside the badge
                fontWeight: "bold",
                fontSize: "0.75rem",
              },
            },
          },
          // MuiSvgIcon: {
          //   styleOverrides: {
          //     root: {
          //       color: dark ? "#fff" : "rgba(0, 0, 0, 0.54)", // Default icon color (affects all icons)
          //     },
          //   },
          // },
        },
      }),
    [dark]
  );

  return (
    <ThemeContext.Provider value={{ dark, setDark }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>{" "}
    </ThemeContext.Provider>
  );
};
